import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAppSelector } from '@/store/hooks';
import { selectIsAuthenticated, selectUserProfile } from '@/store/slices/authSlice';
import { usePermissions } from '@/hooks/usePermissions';
import UserBox from '@/components/UserBox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { EditorControls } from '@/components/editor/EditorControls';
import { Map as MapIcon, SlidersHorizontal, Palette, BookOpen, BookText, Search as SearchIcon, Edit3 } from 'lucide-react';

interface NavItemDef {
  id: string;
  label: string;
  icon: React.ReactElement;
  path: string;
  requiredPermission?: string | string[];
  checkIsActive?: (pathname: string, path: string) => boolean;
}

const TopNavBar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const userProfile = useAppSelector(selectUserProfile);
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  const [pageName, setPageName] = useState<string>("Soda Track");

  const iconDimension = 24; // Icon size in pixels

  const defaultIsActiveCheck = useCallback((pathname: string, path: string) => pathname.startsWith(path), []);

  const navItems: NavItemDef[] = useMemo(() => [
    {
      id: 'map',
      label: 'Places & Sightings',
      icon: <MapIcon size={iconDimension} style={{ minWidth: `${iconDimension}px`, minHeight: `${iconDimension}px` }} />,
      path: '/map',
    },
    {
      id: 'sodaSearch',
      label: 'Soda Finder',
      icon: <SearchIcon size={iconDimension} style={{ minWidth: `${iconDimension}px`, minHeight: `${iconDimension}px` }} />,
      path: '/sodas',
    },
    {
      id: 'diary',
      label: 'My Diary',
      icon: <BookText size={iconDimension} style={{ minWidth: `${iconDimension}px`, minHeight: `${iconDimension}px` }} />,
      path: '/diary',
    },
    {
      id: 'manage',
      label: 'Admin',
      icon: <SlidersHorizontal size={iconDimension} style={{ minWidth: `${iconDimension}px`, minHeight: `${iconDimension}px` }} />,
      path: '/manage',
      requiredPermission: 'manager_view',
    },
    {
      id: 'themes',
      label: 'Themes',
      icon: <Palette size={iconDimension} style={{ minWidth: `${iconDimension}px`, minHeight: `${iconDimension}px` }} />,
      path: '/themes',
      requiredPermission: 'view_technical',
    },
    {
      id: 'docs',
      label: 'Docs',
      icon: <BookOpen size={iconDimension} style={{ minWidth: `${iconDimension}px`, minHeight: `${iconDimension}px` }} />,
      path: '/documentation',
      requiredPermission: 'view_technical',
    },
  ], [iconDimension]);

  useEffect(() => {
    const currentPath = location.pathname;
    const activeNavItem = navItems.find(item => {
      const isActiveCheck = item.checkIsActive || defaultIsActiveCheck;
      return isActiveCheck(currentPath, item.path);
    });

    if (activeNavItem) {
      if (!activeNavItem.requiredPermission) {
        // Public item, always set its label
        setPageName(activeNavItem.label);
      } else {
        // Item requires permission
        // Only attempt to set page name if permissions are loaded and user profile is available
        if (!permissionsLoading && userProfile?.username) {
          if (hasPermission(activeNavItem.requiredPermission)) {
            setPageName(activeNavItem.label);
          } else if (currentPath === "/") { // Not permitted, but if we are on root, set to "Soda Track"
            setPageName("Soda Track");
          }
          // If not permitted and not on root, pageName remains as is.
        } else if (currentPath === "/") {
          // If permissions are still loading (or userProfile not ready) for a restricted item,
          // but we are on the root path, default to "Soda Track".
          setPageName("Soda Track");
        }
        // If on a restricted path and permissions/user profile are loading, pageName will retain its current value until loading completes.
      }
    } else if (currentPath === "/") {
      setPageName("Soda Track"); // No specific item, but it's the home page
    }
    // If no nav item matches and not root, pageName remains (could be "Soda Track" or last valid name).
  }, [location.pathname, navItems, hasPermission, permissionsLoading, userProfile?.username, defaultIsActiveCheck]);

  // Conditional return must come AFTER all hook calls.
  if (!isAuthenticated || !userProfile?.username) {
    return null;
  }

  // Define a CSS class for the button that will accommodate larger icons
  const navButtonClass = "p-2 h-auto w-auto min-h-[56px] min-w-[56px] flex items-center justify-center";

  const handleNavigation = (path: string, pageName: string) => {
    navigate(path);
    setPageName(pageName);
  };

  const renderNavItem = (item: NavItemDef) => {
    const isVisible = !item.requiredPermission || hasPermission(item.requiredPermission);
    if (!isVisible) return null;

    const isActiveCheck = item.checkIsActive || defaultIsActiveCheck;
    const isActive = isActiveCheck(location.pathname, item.path);

    return (
      <TooltipProvider key={item.id} delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={`${navButtonClass} rounded-lg hover:bg-accent focus:bg-accent active:bg-accent data-[active=true]:bg-primary data-[active=true]:text-primary-foreground ${isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
              onClick={() => handleNavigation(item.path, item.label)}
              data-active={isActive}
            >
              {item.icon}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>{item.label}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return (
    <motion.nav
      className="fixed top-0 left-0 right-0 h-16 bg-background/95 backdrop-blur-sm border-b border-border z-50 flex items-center justify-between px-4 shadow-sm"
      initial={{ y: "-100%", opacity: 0 }}
      animate={{ y: "0%", opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
    >
      {/* Left: Logo Text - takes minimal width */}
      <div className="flex-shrink-0">
        <Link to="/" onClick={() => setPageName("Soda Track")} className="flex items-center" aria-label="Home">
          <span className="text-xl font-semibold text-foreground hover:text-primary transition-colors">{pageName}</span>
        </Link>
      </div>

      {/* Center: Navigation Icons - takes remaining space and centers its content */}
      <div className="flex-1 flex items-center justify-center space-x-1 sm:space-x-2">
        {isAuthenticated && userProfile?.username && navItems.map(renderNavItem)}
      </div>
      
      {/* Right: User Box / Auth - takes minimal width */}
      <div className="flex-shrink-0 flex items-center space-x-2">
        {isAuthenticated && userProfile?.username ? (
          <>
            {hasPermission(['editor', 'admin']) && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-full h-12 w-12 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent"
                    aria-label="Editor Settings"
                  >
                    <Edit3 size={24} />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <EditorControls />
                </PopoverContent>
              </Popover>
            )}
            <UserBox variant="compact" />
          </>
        ) : (
          <div className="w-[40px] h-[40px]" /> 
        )}
      </div>
    </motion.nav>
  );
};

export default TopNavBar; 